import React from 'react';
import { Play, Square, Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useTaskTimer, formatDuration } from '@/hooks/useTaskTimer';
import { cn } from '@/lib/utils';

interface TaskTimerButtonProps {
  taskId: string;
  size?: 'sm' | 'default';
  showTotal?: boolean;
  className?: string;
}

export const TaskTimerButton: React.FC<TaskTimerButtonProps> = ({
  taskId,
  size = 'sm',
  showTotal = true,
  className,
}) => {
  const {
    isRunningForThisTask,
    isAnotherTaskRunning,
    elapsedSeconds,
    totalSeconds,
    start,
    stop,
    isPending,
  } = useTaskTimer(taskId);

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (isRunningForThisTask) stop();
    else start();
  };

  return (
    <div className={cn('flex items-center gap-1.5', className)}>
      <Button
        type="button"
        variant={isRunningForThisTask ? 'destructive' : 'outline'}
        size={size}
        onClick={handleClick}
        disabled={isPending}
        className={cn(
          'gap-1.5 h-7 px-2 text-xs',
          isRunningForThisTask && 'animate-pulse'
        )}
        title={
          isRunningForThisTask
            ? 'Detener cronómetro'
            : isAnotherTaskRunning
            ? 'Pausará el cronómetro de otra tarea'
            : 'Iniciar cronómetro'
        }
      >
        {isRunningForThisTask ? (
          <>
            <Square className="h-3 w-3 fill-current" />
            <span className="font-mono">{formatDuration(elapsedSeconds)}</span>
          </>
        ) : (
          <>
            <Play className="h-3 w-3 fill-current" />
            <span>Iniciar</span>
          </>
        )}
      </Button>

      {showTotal && totalSeconds > 0 && !isRunningForThisTask && (
        <Badge variant="secondary" className="h-6 gap-1 text-[10px] font-mono">
          <Timer className="h-3 w-3" />
          {formatDuration(totalSeconds)}
        </Badge>
      )}
    </div>
  );
};
